import {observer} from 'mobx-react-lite'
import React, {useCallback, useMemo, useRef, useState} from 'react'
import {Lang} from 'yougile-lang'
import {Util} from 'yougile-util'
import {Tracking, TrackingEvent} from '@common/tracking'
import {LinkButton} from '@components/common/base/buttons'
import {Menu, MenuItemData} from '@components/common/base/menu'
import {Dropdown} from '@components/common/dropdown/dropdown'
import {crmStore} from '@components/crm/crm-store'
import {canAdd} from '@components/dashboard/dashboard-utils'
import {ImportFromFile} from '@components/import-from-other-services/import-from-file/import-from-file'
import {ImportFromTrello} from '@components/import-from-other-services/import-from-trello/import-from-trello'
import {noPropagate} from '@containers/hooks'
import {globals} from '@extensions/globals-base'
import {
  CompanyProjectsStore,
  useCompanyProjectsStore
} from './company-projects-store'
import XlsIcon from './xls-icon.svg'

const useImportMenu = (
  store: CompanyProjectsStore
): {
  isMenuOpened: boolean
  openMenu: () => void
  closeMenu: () => void
  menuItems: MenuItemData[]
} => {
  const [isMenuOpened, setIsMenuOpened] = useState(false)
  const openMenu = useCallback(() => setIsMenuOpened(true), [setIsMenuOpened])
  const closeMenu = useCallback(() => setIsMenuOpened(false), [setIsMenuOpened])

  const menuItems = useMemo<MenuItemData[]>(() => {
    const isStandalone = globals.standalone
    const canImportFromTrello = !Util.isDesktop()

    const importFromTrelloItem: MenuItemData = {
      id: 'import-from-trello',
      title: Lang.msg.importFromTrello.importFromTrello,
      icon: 'IconBrandTrello',
      iconColor: 'interactive',
      onClick: () => {
        Tracking.send(TrackingEvent.trelloClick)
        store.importFromTrelloOpen()
      },
      disabled: !canImportFromTrello,
      tooltip: canImportFromTrello
        ? ''
        : Lang.msg.importFromTrello.importFromTrelloUseBrowser
    }

    const importFromCsvItem: MenuItemData = {
      id: 'import-from-xls-csv',
      title: Lang.msg.importFromFile.importFromXLSX,
      icon: <XlsIcon />,
      onClick: () => store.importFromFileOpen()
    }

    return isStandalone
      ? [importFromCsvItem]
      : [importFromTrelloItem, importFromCsvItem]
  }, [store])
  return {isMenuOpened, openMenu, closeMenu, menuItems}
}

export const PanelMenu = observer(() => {
  const [state, store] = useCompanyProjectsStore()
  const {menuItems, isMenuOpened, openMenu, closeMenu} = useImportMenu(store)
  const menuBtnRef = useRef(null)

  if (!canAdd()) {
    return null
  }

  const hasMultipleProjectTypes = crmStore.isCrmEnabled

  return (
    <div className="mb-16 flex flex-row" onClick={noPropagate}>
      <div className="flex gap-16 mr-16">
        <LinkButton
          icon="IconCirclePlus"
          size="small"
          onClick={store.newProjOpen}
        >
          {hasMultipleProjectTypes
            ? Lang.msg.addProjectWithTasks
            : Lang.msg.addProject}
        </LinkButton>

        {crmStore.isCrmEnabled && (
          <LinkButton
            icon="IconCirclePlus"
            size="small"
            onClick={() => {
              Tracking.send(TrackingEvent.crmClickOnAddCrmProjectButton)
              store.newCrmProjOpen()
            }}
          >
            {Lang.msg.crm.addProject}
          </LinkButton>
        )}
      </div>

      <LinkButton
        ref={menuBtnRef}
        icon="IconFileImport"
        size="small"
        className="ml-auto"
        onClick={openMenu}
      >
        {Lang.msg.importFromFile.importFromOtherServices}
      </LinkButton>
      {isMenuOpened && (
        <Dropdown
          reference={menuBtnRef}
          placement="bottom"
          hideArrow
          visible
          onVisibleChange={closeMenu}
          theme="new-menu"
          content={<Menu items={menuItems} onItemClicked={closeMenu} />}
        />
      )}

      <ImportFromFile
        isOpen={state.importFromFileIsOpened}
        setIsOpen={store.setImportFromFileIsOpened}
      />
      <ImportFromTrello
        isOpen={state.importFromTrelloIsOpened}
        setIsOpen={store.setImportFromTrelloIsOpened}
      />
    </div>
  )
})
